import { createRouter, createWebHistory } from 'vue-router';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';

// Retry wrapper for lazy-loaded chunks to handle stale cache after deployments
function lazyLoad(importFn) {
  return () => importFn().catch(() => {
    // Chunk failed to load (stale cache after deploy) - reload to get fresh assets
    window.location.reload();
  });
}

// Lazy load components for better performance and smaller initial bundle
const AuthDemo = lazyLoad(() => import('../components/AuthDemo.vue'));
const AdaptiveQuiz = lazyLoad(() => import('../components/AdaptiveQuiz.vue'));
const ProgressDashboard = lazyLoad(() => import('../components/ProgressDashboard.vue'));
const PremiumSubscription = lazyLoad(() => import('../components/PremiumSubscription.vue'));
const PdfExport = lazyLoad(() => import('../components/PdfExport.vue'));
const QuestionDatabase = lazyLoad(() => import('../components/QuestionDatabase.vue'));
const UserMenu = lazyLoad(() => import('../components/UserMenu.vue'));
const ProfileCompletion = lazyLoad(() => import('../components/ProfileCompletion.vue'));
const Achievements = lazyLoad(() => import('../components/Achievements.vue'));
const AdvancedAnalytics = lazyLoad(() => import('../components/AdvancedAnalytics.vue'));
const StudyGroups = lazyLoad(() => import('../components/StudyGroups.vue'));
const SocialFeed = lazyLoad(() => import('../components/SocialFeed.vue'));
const FreeTrialLanding = lazyLoad(() => import('../components/FreeTrialLanding.vue'));
const KidDashboard = lazyLoad(() => import('../components/KidDashboard.vue'));
const KidQuiz = lazyLoad(() => import('../components/KidQuiz.vue'));

// Base routes available in all environments
const baseRoutes = [
  { 
    path: '/', 
    name: 'Auth',
    component: AuthDemo,
    meta: { requiresAuth: false }
  },
  { 
    path: '/dashboard', 
    name: 'Dashboard',
    component: ProgressDashboard, 
    meta: { requiresAuth: true }
  },
  { 
    path: '/quiz', 
    name: 'Quiz',
    component: AdaptiveQuiz, 
    meta: { requiresAuth: true }
  },
  { 
    path: '/subscription', 
    name: 'Subscription',
    component: PremiumSubscription, 
    meta: { requiresAuth: true }
  },
  { 
    path: '/export', 
    name: 'Export',
    component: PdfExport, 
    meta: { requiresAuth: true }
  },
  { 
    path: '/admin/questions', 
    name: 'QuestionDatabase',
    component: QuestionDatabase, 
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  { 
    path: '/profile', 
    name: 'Profile',
    component: ProfileCompletion, 
    meta: { requiresAuth: true }
  },
  { 
    path: '/achievements', 
    name: 'Achievements',
    component: Achievements, 
    meta: { requiresAuth: true }
  },
  { 
    path: '/analytics', 
    name: 'Analytics',
    component: AdvancedAnalytics, 
    meta: { requiresAuth: true, requiresPremium: true }
  },
  { 
    path: '/groups', 
    name: 'StudyGroups',
    component: StudyGroups, 
    meta: { requiresAuth: true, requiresPremium: true }
  },
  { 
    path: '/social', 
    name: 'SocialFeed',
    component: SocialFeed, 
    meta: { requiresAuth: true, requiresPremium: true }
  },
  { 
    path: '/free-trial', 
    name: 'FreeTrial',
    component: FreeTrialLanding, 
    meta: { requiresAuth: false }
  },
  // Legal pages
  { 
    path: '/terms', 
    name: 'Terms',
    component: lazyLoad(() => import('../components/LegalPages.vue')),
    meta: { requiresAuth: false }
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: lazyLoad(() => import('../components/LegalPages.vue')),
    meta: { requiresAuth: false }
  },
  // Standards page
  {
    path: '/standards',
    name: 'Standards',
    component: lazyLoad(() => import('../views/Standards.vue')),
    meta: { requiresAuth: false }
  },
  // Kid-friendly routes (K-2)
  { 
    path: '/kid-dashboard', 
    name: 'KidDashboard',
    component: KidDashboard, 
    meta: { requiresAuth: true, requiresKidGrade: true }
  },
  { 
    path: '/kid-quiz/:subject', 
    name: 'KidQuiz',
    component: KidQuiz, 
    meta: { requiresAuth: true, requiresKidGrade: true }
  },
  // Catch all route - redirect to auth
  { 
    path: '/:pathMatch(.*)*', 
    redirect: '/'
  }
];

// Use base routes for production
const routes = baseRoutes;

const router = createRouter({
  history: createWebHistory(),
  routes
});

// Handle chunk load errors (stale cache after deployment)
router.onError((error, to) => {
  if (error.message?.includes('Loading chunk') ||
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.name === 'ChunkLoadError') {
    // Navigate to the intended route with a full page reload to get fresh assets
    window.location.href = to?.fullPath || window.location.href;
  }
});

// Navigation guard
router.beforeEach(async (to, from, next) => {
  // Check if route requires authentication
  if (to.meta.requiresAuth) {
    // Wait for auth state to be determined
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        unsubscribe();
        
        if (!user) {
          // Not authenticated, redirect to login
          next('/');
          resolve();
        } else if (to.meta.requiresPremium) {
          // Allow access to premium features
          next();
          resolve();
        } else if (to.meta.requiresAdmin) {
          // Check if user is admin (specific email)
          if (user.email === 'joeperrotta1979@gmail.com') {
            next();
            resolve();
          } else {
            // Not admin, redirect to dashboard
            next('/dashboard');
            resolve();
          }
        } else {
          // Authenticated and no special requirements
          next();
          resolve();
        }
      });
    });
  } else {
    // Route doesn't require auth
    next();
  }
});

export default router; 